import type { ButtonHTMLAttributes, ComponentChildren, JSX, Ref } from "preact";
import { forwardRef } from "preact/compat";
import { Link } from "wouter-preact";
import { classNames } from "@gss/common/classNames";
import "./Button.css";
import type { Variant } from "@gss/types/Variant";

export type ButtonVariant = Variant | "link" | "default" | `outline-${Variant}`;

interface ButtonProps extends ButtonHTMLAttributes {
    variant: ButtonVariant | null;
    small?: boolean;
    className?: string;
}

export const Button = forwardRef(
    (
        { variant, small, className, children, ...rest }: ButtonProps,
        ref: Ref<HTMLButtonElement>,
    ): JSX.Element => {
        return (
            <button
                ref={ref}
                type="button"
                className={classNames(
                    "btn",
                    variant != null && getVariantClass(variant),
                    small && "btn-sm",
                    className,
                )}
                {...rest}
            >
                {children}
            </button>
        );
    },
);

Button.displayName = "Button";

interface LinkProps {
    variant: ButtonVariant | null;
    href: string;
    small?: boolean;
    className?: string;
    children: ComponentChildren;
}

export const LinkButton = forwardRef(
    (
        { variant, small, className, children, href, ...rest }: LinkProps,
        ref: Ref<EventTarget>,
    ): JSX.Element => {
        return (
            <Link
                ref={ref}
                href={href}
                className={getClassName(variant, small, className)}
                {...rest}
            >
                {children}
            </Link>
        );
    },
);

LinkButton.displayName = "LinkButton";

function getClassName(
    variant: ButtonVariant | null,
    small: boolean | undefined,
    className: string | undefined,
): string | undefined {
    return classNames(
        "btn",
        variant != null && getVariantClass(variant),
        small && "btn-sm",
        className,
    );
}

function getVariantClass(variant: ButtonVariant): string {
    switch (variant) {
        case "primary":
            return "btn-primary";
        case "secondary":
            return "btn-secondary";
        case "success":
            return "btn-success";
        case "danger":
            return "btn-danger";
        case "warning":
            return "btn-warning";
        case "info":
            return "btn-info";
        case "light":
            return "btn-light";
        case "dark":
            return "btn-dark";

        case "link":
            return "btn-link";
        case "default":
            return "btn-default";

        case "outline-primary":
            return "btn-outline-primary";
        case "outline-secondary":
            return "btn-outline-secondary";
        case "outline-success":
            return "btn-outline-success";
        case "outline-danger":
            return "btn-outline-danger";
        case "outline-warning":
            return "btn-outline-warning";
        case "outline-info":
            return "btn-outline-info";
        case "outline-light":
            return "btn-outline-light";
        case "outline-dark":
            return "btn-outline-dark";

        default: {
            const _ensureExhaustive: never = variant;
            throw new Error("Unknown Button variant");
        }
    }
}
